import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Anchor, Layers, Layers2, Volleyball } from "lucide-react";
import { Button } from "@/components/ui/button";

type Court = "tenis" | "poli";
type Floor = "asfaltico" | "concreto";
type IconType = "anchor" | "layers2" | "layers" | "flex" | "volleyball" | "tennis" | "demarc";

const FlexIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 4h14" />
    <path d="M5 20h14" />
    <path d="M16 4 8 8l8 4-8 4 8 4" />
  </svg>
);

const DemarcIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h16" />
    <path d="M4 17h16" />
    <path d="M12 4v16" />
  </svg>
);

const TennisBallIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx={12} cy={12} r={9} />
    <path d="M5.6 5.6C9 9 9 15 5.6 18.4" />
    <path d="M18.4 5.6C15 9 15 15 18.4 18.4" />
  </svg>
);

function ProductIcon({ type }: { type: IconType }) {
  const cls = "h-[22px] w-[22px]";
  if (type === "anchor") return <Anchor className={cls} />;
  if (type === "layers2") return <Layers2 className={cls} />;
  if (type === "layers") return <Layers className={cls} />;
  if (type === "flex") return <FlexIcon />;
  if (type === "volleyball") return <Volleyball className={cls} />;
  if (type === "tennis") return <TennisBallIcon />;
  if (type === "demarc") return <DemarcIcon />;
  return null;
}

const PRODUCTS: { idx: string; stage: string; name: string; fn: string; iconType: IconType; swatch: string; optional?: boolean }[] = [
  { idx: "01", stage: "Preparação",    name: "Primer de Ancoragem",             fn: "Ponte química entre base e acabamento",          iconType: "anchor",     swatch: "#ECE9E1" },
  { idx: "02", stage: "Regularização", name: "Lama Asfáltica",                  fn: "Regularização de bases asfálticas",              iconType: "layers2",    swatch: "#2c2c30" },
  { idx: "03", stage: "Regularização", name: "Base Regularizadora",             fn: "Regulariza a textura para a pintura",            iconType: "layers",     swatch: "#9aa0a6" },
  { idx: "04", stage: "Performance",   name: "Camada Flex",                     fn: "Amortecimento e conforto do atleta",             iconType: "flex",       swatch: "#3f4b57", optional: true },
  { idx: "05", stage: "Acabamento",    name: "Resina Acrílica Indusflex — Poliesportiva", fn: "Acabamento técnico para quadras poliesportivas", iconType: "volleyball", swatch: "#1f7a4d" },
  { idx: "06", stage: "Acabamento",    name: "Resina Acrílica Indusflex — Tênis",         fn: "Acabamento especializado para quadras de tênis", iconType: "tennis",     swatch: "#c0492f" },
  { idx: "07", stage: "Demarcação",    name: "Indusflex Demarcatória",                    fn: "Linhas e marcações com bordas nítidas",          iconType: "demarc",     swatch: "#f4f4f5" },
];

function systemLayers(court: Court, floor: Floor) {
  const ids: string[] = [];
  ids.push("07");                                       // Demarcatória (topo, última camada aplicada)
  ids.push(court === "tenis" ? "06" : "05");            // Resina
  if (floor === "asfaltico" || court === "tenis") ids.push("03");  // Base Regularizadora
  if (floor === "asfaltico") ids.push("02");           // Lama Asfáltica
  if (floor === "asfaltico") ids.push("04");           // Camada Flex (opcional)
  ids.push("01");                                       // Primer (base)
  return ids.map((id) => PRODUCTS.find((p) => p.idx === id)!);
}

const SistemaConfigurador = () => {
  const [court, setCourt] = useState<Court>("tenis");
  const [floor, setFloor] = useState<Floor>("asfaltico");

  const layers = systemLayers(court, floor);
  const courtLabel = court === "tenis" ? "Tênis" : "Poliesportiva";
  const floorLabel = floor === "asfaltico" ? "asfáltico" : "concreto";

  return (
    <section className="bg-surface-muted py-[76px]">
      <div className="container max-w-[1120px]">
        {/* Header */}
        <div className="mb-12 max-w-[760px]">
          <span className="inline-block font-mono text-[0.72rem] uppercase tracking-[0.22em] text-accent">
            Produtos
          </span>
          <h2 className="mt-[14px] text-[1.7rem] font-extrabold leading-[1.08] tracking-[-0.045em] md:text-[2.3rem]">
            Monte o sistema de pintura da sua quadra.
          </h2>
          <p className="mt-4 max-w-[56ch] leading-relaxed text-muted-foreground">
            A performance da superfície esportiva depende do conjunto — e cada produto entra numa ordem certa, do preparo da base ao acabamento. Selecione o tipo de quadra e o piso para ver as camadas do sistema.
          </p>
        </div>

        {/* Segmented controls */}
        <div className="mb-[18px] flex flex-wrap gap-6 md:gap-9">
          <div>
            <span className="mb-[9px] block font-mono text-[0.64rem] uppercase tracking-[0.16em] text-muted-foreground">
              Tipo de quadra
            </span>
            <div className="inline-flex overflow-hidden rounded-lg border border-border bg-card">
              {(["tenis", "poli"] as const).map((val, i) => (
                <button
                  key={val}
                  onClick={() => setCourt(val)}
                  className={[
                    "px-[22px] py-[11px] text-[0.88rem] font-semibold transition-colors duration-200",
                    i > 0 ? "border-l border-border" : "",
                    court === val
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {val === "tenis" ? "Tênis" : "Poliesportiva"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-[9px] block font-mono text-[0.64rem] uppercase tracking-[0.16em] text-muted-foreground">
              Tipo de piso
            </span>
            <div className="inline-flex overflow-hidden rounded-lg border border-border bg-card">
              {(["asfaltico", "concreto"] as const).map((val, i) => (
                <button
                  key={val}
                  onClick={() => setFloor(val)}
                  className={[
                    "px-[22px] py-[11px] text-[0.88rem] font-semibold transition-colors duration-200",
                    i > 0 ? "border-l border-border" : "",
                    floor === val
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {val === "asfaltico" ? "Asfáltico" : "Concreto"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary line */}
        <div className="mb-5 flex flex-wrap items-center gap-3 text-[0.95rem] text-muted-foreground">
          <span>
            Sistema para quadra de{" "}
            <strong className="font-bold text-primary">{courtLabel}</strong> em piso{" "}
            <strong className="font-bold text-primary">{floorLabel}</strong>
          </span>
          <span className="rounded-full bg-accent px-[10px] py-1 font-mono text-[0.64rem] uppercase tracking-[0.1em] text-white">
            {layers.length} camadas
          </span>
        </div>

        {/* Stack */}
        <div className="mx-auto max-w-[820px]">
          {/* Top orientation tag */}
          <div className="mb-[10px] flex items-center gap-2 font-mono text-[0.64rem] uppercase tracking-[0.12em] text-muted-foreground">
            <span className="h-px w-[34px] bg-border" />
            Topo · contato com o atleta
          </div>

          <div className="flex flex-col">
            <AnimatePresence mode="popLayout" initial={false}>
              {layers.map((product, i) => (
                <motion.div
                  key={product.idx}
                  layout
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 14 }}
                  transition={{ duration: 0.2, delay: i * 0.04 }}
                  whileHover={{ x: 8 }}
                  className="relative grid items-center gap-[22px] border border-border bg-card px-[26px] py-5 shadow-[0_10px_22px_-16px_rgba(15,23,42,0.4)] transition-shadow duration-[250ms] hover:shadow-[-8px_14px_28px_-16px_rgba(0,95,85,0.5)] hover:z-[2]"
                  style={{
                    marginTop: i === 0 ? 0 : -1,
                    gridTemplateColumns: "auto 1fr auto",
                    borderTopLeftRadius: i === 0 ? 10 : 0,
                    borderTopRightRadius: i === 0 ? 10 : 0,
                    borderBottomLeftRadius: i === layers.length - 1 ? 10 : 0,
                    borderBottomRightRadius: i === layers.length - 1 ? 10 : 0,
                  }}
                >
                  {/* Edge color strip */}
                  <div
                    className="absolute inset-y-0 left-0 w-[6px]"
                    style={{
                      background: product.swatch,
                      boxShadow: "inset -1px 0 0 rgba(0,0,0,0.10)",
                      borderTopLeftRadius: i === 0 ? 10 : 0,
                      borderBottomLeftRadius: i === layers.length - 1 ? 10 : 0,
                    }}
                  />

                  {/* Depth */}
                  <div className="w-[78px] font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted-foreground">
                    <b className="block text-[1.5rem] font-bold leading-none tracking-[-0.03em] text-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </b>
                    camada
                  </div>

                  {/* Name + fn */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-accent">
                        {product.stage}
                      </span>
                      {product.optional && (
                        <span className="rounded-full border border-border px-[7px] py-[1px] font-mono text-[0.58rem] uppercase tracking-[0.12em] text-muted-foreground">
                          Opcional
                        </span>
                      )}
                    </div>
                    <h3 className="text-[1.05rem] font-bold leading-[1.15]">{product.name}</h3>
                    <p className="mt-[3px] text-[0.84rem] text-muted-foreground">{product.fn}</p>
                  </div>

                  {/* Icon */}
                  <div
                    className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full text-primary"
                    style={{ background: "hsl(172 100% 19% / 0.08)" }}
                  >
                    <ProductIcon type={product.iconType} />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Bottom orientation tag */}
          <div className="mt-[10px] flex items-center justify-end gap-2 font-mono text-[0.64rem] uppercase tracking-[0.12em] text-muted-foreground">
            Base · piso existente
            <span className="h-px w-[34px] bg-border" />
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 flex justify-center">
          <Link to="/produtos">
            <Button variant="cta-outline" size="lg">
              Ver todos os produtos <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SistemaConfigurador;
